import { DafStepUpAuthStrategy, DafStepUpAuthStrategyConfig } from '@nab/x-auth';
import { DafAuthConfig, DafAuthToken, authenticate, TOKEN_SOURCE } from '@nab/x-auth-core';
import omit from 'lodash/omit';
import { genIdentityAppTag, resolveIdentityAppTag } from '../../util';
import { CustomDafAuthConfig } from '../../interfaces';

/**
 * @class CustomDafStepUpAuthStrategy
 * A custom auth strategy that extends DafStepUpAuthStrategy to provide flexibility
 * in choosing the parent token source based on the `useParentToken` property.
 * 
 * - When `useParentToken` is `false` (default): Uses the mini-app's exchange token to step up
 * - When `useParentToken` is `true`: Uses the configured parentTokenAuthConfig to step up the shell seed token
 * 
 * This allows for dynamic parent token selection at runtime based on the authentication context.
 */
export class CustomDafStepUpAuthStrategy<
    AUTH extends CustomDafAuthConfig = CustomDafAuthConfig,
    PARENT_AUTH extends DafAuthConfig = DafAuthConfig
> extends DafStepUpAuthStrategy<AUTH, PARENT_AUTH> {
    private shellTokenAuthConfig: PARENT_AUTH;

    constructor(config: DafStepUpAuthStrategyConfig<PARENT_AUTH>) {
        // Store shell token config separately, don't pass it to parent
        const shellConfig = config.parentTokenAuthConfig;
        const configWithoutParent = { ...config, parentTokenAuthConfig: undefined };
        
        super(configWithoutParent);
        this.shellTokenAuthConfig = shellConfig;
    }

    /**
     * Determines the parent token to use based on the `useParentToken` and `isSeed` properties in the config.
     *  
     * Behavior:
     * - If `useParentToken` is `true` OR `isSeed` is `true`: Uses the shell seed token
     * - Otherwise (default): Uses the current authConfig which leads to retrieving the mini-app token
     */
    private getParentTokenWithFlag(config: AUTH) {
        const shouldUseParentTokenConfig = config.useParentToken === true;
        const isSeedTokenRequest = config.isSeed === true;

        // If explicitly requesting shell token via useParentToken or isSeed flags
        if ((shouldUseParentTokenConfig || isSeedTokenRequest) && this.shellTokenAuthConfig) {
            return authenticate(this.shellTokenAuthConfig);
        }

        // Default behavior: use the mini-app's exchange token
        // Omit flags to avoid recursive calls to DafStepUpAuthStrategy
        return authenticate(omit(config, 'flags'));
    }

    public supports(config: AUTH) {
        return (
            (config.tokenSource === TOKEN_SOURCE.DAF || config.tokenSource === TOKEN_SOURCE.MOBILE) &&
            config.flags?.stepUp === '2FA' &&
            config.tokenType === 'Bearer'
        );
    }

    public async authenticate<T = DafAuthToken<AUTH>>(config: AUTH): Promise<T> {
        const reauthTag = resolveIdentityAppTag('nab-idp-reauthentication', config?.designSystem);
        const { appMetaData } = genIdentityAppTag(reauthTag);
        // @ts-ignore - accessing protected property from parent class
        this.initConfig.linkedAppConfig.appMetaData = {
            // @ts-ignore - accessing protected property from parent class
            ...this.initConfig.linkedAppConfig.appMetaData,
            ...appMetaData,
        };

        const { tokenValue: tokenToStepUp, id: parentId, config: parentTokenConfig = {} as DafAuthConfig } = await this.getParentTokenWithFlag(config);

        // @ts-ignore - accessing protected property from parent class
        const baseAppConfig = this.initConfig.linkedAppConfig.appConfig;

        // Determine which clientId and scopes to use
        let clientId: string;
        let scope: string;

        const isUsingShellToken = (config.useParentToken === true || config.isSeed === true) && this.shellTokenAuthConfig;
        
        if (isUsingShellToken) {
            // When using shell token, use shell clientId/scopes from shellTokenAuthConfig
            clientId = this.shellTokenAuthConfig.clientId;
            scope = (this.shellTokenAuthConfig.scopes as string[]).join(' ');
        } else {
            // When NOT using shell token, use mini-app's existing clientId and scopes as-is
            clientId = parentTokenConfig.clientId;
            scope = parentTokenConfig.scopes ? (parentTokenConfig.scopes as string[]).join(' ') : '';
        }

        // @ts-ignore - accessing protected property from parent class
        this.initConfig.linkedAppConfig.appConfig = {
            ...baseAppConfig,
            parentToken: tokenToStepUp,
            clientId,
            scope
        };

        // super.authenticate() returns a complete token object, not a payload
        const tokenFromIdentityApp = await super.authenticate(config);

        // Update the token's config with the scopes we actually requested for step-up
        const updatedConfig = {
            ...tokenFromIdentityApp.config,
            clientId,
            scopes: scope.split(' '),
            flags: {
                ...tokenFromIdentityApp.config.flags,
                stepUp: '2FA'
            }
        };

        const steppedUpToken = {
            ...tokenFromIdentityApp,
            config: updatedConfig
        };

        return steppedUpToken as unknown as T;
    }
}

